import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { MonitorResponse, MonitorStage, QueueRunDetail } from "./types";
import { useQueueResource } from "./useQueueResource";

type ArtifactResponse = MonitorResponse & {
  artifact: {
    reference_id: string;
    kind: "composer_script";
    label: string;
    render_as: "text";
    media_type: "application/json";
    redacted: boolean;
    text: string;
    sha256: string;
    source_bytes: number;
    execution_epoch: number;
  };
};

export function PipelineArtifacts({
  stage,
  detail,
}: {
  stage: MonitorStage;
  detail: QueueRunDetail;
}) {
  if (!detail.capabilities.artifacts || !stage.artifacts?.length) return null;
  return (
    <section aria-label="Committed script evidence" className="space-y-2">
      <h4 className="font-medium">Committed script evidence</h4>
      {stage.artifacts.map((artifact) => (
        <ArtifactPreview
          key={artifact.reference_id}
          artifact={artifact}
          stage={stage}
          detail={detail}
        />
      ))}
    </section>
  );
}

function ArtifactPreview({
  artifact,
  stage,
  detail,
}: {
  artifact: NonNullable<MonitorStage["artifacts"]>[number];
  stage: MonitorStage;
  detail: QueueRunDetail;
}) {
  const [open, setOpen] = useState(false);
  const path = `/pipeline/runs/${encodeURIComponent(detail.run.workload_id)}/stages/${encodeURIComponent(stage.stage_instance_id)}/artifacts/${encodeURIComponent(artifact.reference_id)}?epoch=${detail.run.execution_epoch}`;
  const resource = useQueueResource<ArtifactResponse>(
    open ? path : null,
    "artifact",
  );
  const result = resource.data?.artifact;
  const verified =
    result &&
    result.reference_id === artifact.reference_id &&
    result.execution_epoch === detail.run.execution_epoch &&
    resource.data?.environment === detail.environment &&
    resource.data?.projection_generation === detail.projection_generation &&
    result.render_as === "text" &&
    result.media_type === "application/json" &&
    typeof result.text === "string" &&
    /^[a-f0-9]{64}$/.test(result.sha256);
  return (
    <div className="space-y-2 rounded border p-2">
      <p>
        {artifact.direction === "input" ? "Input" : "Output"} · {artifact.label}
      </p>
      <Button
        variant="outline"
        size="sm"
        aria-expanded={open}
        onClick={() => {
          if (!open) resource.reload();
          setOpen((value) => !value);
        }}
      >
        {open ? "Close preview" : "Open script preview"}
      </Button>
      {open && (
        <>
          {resource.loading && (
            <p role="status">Checking access and evidence…</p>
          )}
          {resource.error || (resource.data && !verified) ? (
            <p role="status">
              Script evidence is unavailable. It may not be committed yet, may
              have expired, or access may have changed.
            </p>
          ) : (
            verified && (
              <>
                <p className="text-xs text-muted-foreground">
                  Verified against the committed launch. Private references and
                  diagnostic fields are omitted.
                </p>
                <pre
                  className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs"
                  aria-label="Script preview"
                >
                  {result.text}
                </pre>
                <p className="break-all font-mono text-xs">
                  SHA-256: {result.sha256}
                </p>
              </>
            )
          )}
          <Button variant="link" size="sm" onClick={resource.reload}>
            Refresh evidence
          </Button>
        </>
      )}
    </div>
  );
}
